
import Vue from 'vue';
import { annotationSourceReference, canAnnotateImage } from '@/utils/annotationSource';
import { mapActions, mapGetters, mapState } from 'vuex';
import { RunnitBulkActionsMixin } from '@/mixins/RunnitBulkActionsMixin';
import RunnitSelectedImage from '@/views/Runnits/RunnitSelectedImage.vue';
import RunnitSettingHelperBtn from '@/views/Runnits/RunnitSettings/RunnitSettingHelperBtn.vue';
import ImageUpload from "@/views/Runnits/RunnitSettings/ImageInput/ImageUpload.vue";
import Use3DTooltipCard from '@/views/Runnits/RunnitSettings/Pose3DEditor/Use3DTooltipCard.vue';
import {
	IMAGE_UPLOAD_MODE,
	UploadFile,
} from '@run-diffusion/shared';
import { RunnitsImageSelectMixin } from "@/mixins/RunnitsImageSelectMixin";
import { TeamUserMixin } from '@/mixins';

export default Vue.extend({
	name: 'ImageInputSelectedImageContainer',
	props: {
		value: { type: Object, default: null },
		libraryText: { type: String, default: 'Library' },
		hasInpainting: { type: Boolean, default: false },
		hasVisualPrompt: { type: Boolean, default: false },
		hasStrength: { type: Boolean, default: false },
		hasPose3D: { type: Boolean, default: false },
		fieldUuid: { type: String, default: null },
	},
	mixins: [
		RunnitBulkActionsMixin,
		RunnitsImageSelectMixin,
		TeamUserMixin,
	],
	computed: {
		...mapState([
			'user',
			'runnitState',
			'dragAndDrop',
		]),
		...mapGetters([
			'getInpaintingEditorState',
		]),
		getVisualPromptEditorState () {
			const states = this.runnitState.visualPromptEditorStates;
			return new Proxy(states, {
				get: (target, prop: string) => {
					return target[prop] || {
						open: false,
						saving: false,
						backgroundImageUrl: null,
						replaceImageTarget: null,
					};
				},
			});
		},
		canAnnotateSelectedImage (): boolean {
			return canAnnotateImage(this.imageValue?.nodeRunResult || this.imageValue?.upload);
		},
		isImageSelected () {
			return !!(
				this.imageValue &&
				(
					this.imageValue.nodeRunResult ||
					this.imageValue.upload
				)
			);
		},
		imageValue () {
			let val = this.value
			if (this.hasInpainting || this.hasStrength) {
				val = this._get(this.value, 'image') || null;
			}
			return val;
		},
		inpaintingValue () {
			if (this.hasInpainting) {
				return this._get(this.value, 'inpainting') || { mask: null };
			}
			return null;
		},
		strengthValue () {
			if (this.hasStrength) {
				return this._get(this.value, 'strength') || 0.8;
			}
			return null;
		},
		rightPercentage () {
			// Convert 0-1 range to percentage
			return Math.round((this.strengthValue || 0) * 100);
		},
		leftPercentage () {
			// Always 100 - right percentage to ensure they add up to 100%
			return 100 - this.rightPercentage;
		},
		leftIcon () {
			return 'mdi-image-outline';
		},
		rightIcon () {
			return 'mdi-text';
		},
		leftIconSize () {
			const minSize = 18;
			const maxSize = 24;
			const strengthPercent = this.strengthValue || 0;
			const size = maxSize - (strengthPercent * (maxSize - minSize));
			return `${size}px`;
		},
		rightIconSize () {
			const minSize = 18;
			const maxSize = 24;
			const strengthPercent = this.strengthValue || 0;
			const size = minSize + (strengthPercent * (maxSize - minSize));
			return `${size}px`;
		},
	},
	data () {
		return {
			IMAGE_UPLOAD_MODE,
			showMaskOverBackground: false,
		};
	},
	methods: {
		...mapActions([
			'updateRunnitState',
			'updateInpaintingEditorState',
			'removeInpaintingEditorState',
			'updateVisualPromptEditorState',
			'removeVisualPromptEditorState',
			'updatePose3DEditorState',
		]),
		onClear () {
			this.$emit('on-clear');
		},
		onLibrary () {
			this.$emit('on-library');
		},
		async fetchImageUrl () {
			if (this.imageValue.nodeRunResult) {
				const { imageUrl } = await this.fetchSrc(this.imageValue.nodeRunResult, 'nodeRunResult');
				return imageUrl;
			}
			if (this.imageValue.upload) {
				const { imageUrl } = await this.fetchSrc(this.imageValue.upload, 'upload');
				return imageUrl;
			}
		},
		async handleInpaintingClick () {
			const currentState = this.getInpaintingEditorState(this.fieldUuid);
			if (currentState.saving) return;

			const imageUrl = await this.fetchImageUrl();

			// Close other menus globally
			this.updateRunnitState({
				promptGeneratorOpen: false,
				imageSelectOpen: false,
			});

			// Update field-specific state
			if (currentState.open) {
				this.removeInpaintingEditorState(this.fieldUuid);
			} else {
				this.updateInpaintingEditorState({
					fieldUuid: this.fieldUuid,
					editorState: {
						open: true,
						backgroundImageUrl: imageUrl,
					},
				});
			}
		},
		async handleVisualPromptClick () {
			const currentState = this.getVisualPromptEditorState[this.fieldUuid];
			if (currentState.saving || !this.canAnnotateSelectedImage) return;
			const baseSource = annotationSourceReference(this.imageValue.nodeRunResult || this.imageValue.upload, this.imageValue.nodeRunResult ? 'nodeRunResult' : 'upload');
			if (!baseSource) return;

			const imageUrl = await this.fetchImageUrl();

			// Close other menus globally
			this.updateRunnitState({
				promptGeneratorOpen: false,
				imageSelectOpen: false,
			});

			// Update field-specific state
			if (currentState.open) {
				this.removeVisualPromptEditorState(this.fieldUuid);
			} else {
				this.updateVisualPromptEditorState({
					fieldUuid: this.fieldUuid,
					editorState: {
						open: true,
						backgroundImageUrl: imageUrl,
						baseSource,
					},
				});
			}
		},
		onStrengthInput (strengthVal: number) {
			this.$emit('on-strength-change', strengthVal);
		},
		handleImageUpload (upload: UploadFile) {
			this.$emit('on-upload', upload);
		},
		toggleImageSelectionMode () {
			this.$emit('on-toggle-image-selection-mode');
		},
		onUse3D () {
			const input = this.$refs.pose3dFileInput as HTMLInputElement | undefined;
			if (input) input.click();
		},
		onPose3DFileSelected (ev: Event) {
			const target = ev.target as HTMLInputElement;
			const file = target.files && target.files[0];
			target.value = '';
			if (!file || !this.fieldUuid) return;
			this.updatePose3DEditorState({
				fieldUuid: this.fieldUuid,
				editorState: {
					open: true,
					saving: false,
					assetFile: file,
					assetName: file.name,
				},
			});
		},
	},
	components: {
		ImageUpload,
		RunnitSelectedImage,
		RunnitSettingHelperBtn,
		Use3DTooltipCard,
	},
});
